import { EventTypes } from '@groundcover/browser'

interface SanitizationConfig {
  sensitiveHeaders: Set<string>
  maskValue: string
}

const DEFAULT_CONFIG: SanitizationConfig = {
  sensitiveHeaders: new Set(['x-auth-token', 'unscopedtoken']),
  maskValue: '***',
}

type AnyEvent = EventTypes[keyof EventTypes]

// Hybrid attributes structure (Groundcover uses mix of flat & nested keys)
type FlatAttributes = Record<string, unknown> & {
  location?: {
    path?: string
    url?: string
    title?: string
  }
}

const sanitizeUrlString = (
  urlString: string,
): { url: string; pathname: string; modified: boolean } | null => {
  try {
    const url = new URL(urlString)
    const hadParams = url.search.length > 0

    // Remove all query params
    url.search = ''

    return {
      url: url.toString(),
      pathname: url.pathname,
      modified: hadParams,
    }
  } catch {
    // Ignore invalid URLs
    return null
  }
}

const sanitizeUrls = (attrs: FlatAttributes) => {
  // Network URL (flat key): url.full
  if (attrs['url.full']) {
    const sanitized = sanitizeUrlString(attrs['url.full'] as string)
    if (sanitized) {
      attrs['url.full'] = sanitized.url

      if (sanitized.modified) {
        attrs['http.path'] = sanitized.pathname
        attrs['http.route'] = sanitized.pathname
      }
    }
  }

  // Navigation / Page URLs: location.url (nested object)
  if (attrs.location?.url) {
    const sanitized = sanitizeUrlString(attrs.location.url)
    if (sanitized) {
      attrs.location.url = sanitized.url

      if (sanitized.modified) {
        attrs.location.path = sanitized.pathname
      }
    }
  }
}

const sanitizeHeaders = (attrs: FlatAttributes, config: SanitizationConfig): void => {
  for (const key of Object.keys(attrs)) {
    // Matches patterns like:
    // http.request.headers.x-auth-token
    // http.response.headers.UnscopedToken
    const headerMatch = key.match(/^http\.(request|response)\.headers\.(.+)$/i)
    if (!headerMatch) continue

    const headerName = headerMatch[2].toLowerCase()
    if (config.sensitiveHeaders.has(headerName)) {
      attrs[key] = config.maskValue
    }
  }
}

export const sanitizeGroundcoverEvent = (
  event: AnyEvent,
  config: SanitizationConfig = DEFAULT_CONFIG,
): AnyEvent => {
  const attrs = event.attributes as FlatAttributes

  // Sanitize URLs for ALL events (remove all query params)
  sanitizeUrls(attrs)

  // Sanitize headers ONLY for network events
  if (event.type === 'network') {
    sanitizeHeaders(attrs, config)
  }

  return event
}

export const createSanitizer = (customConfig: Partial<SanitizationConfig>) => {
  const mergedConfig: SanitizationConfig = {
    maskValue: customConfig.maskValue ?? DEFAULT_CONFIG.maskValue,
    sensitiveHeaders: new Set([
      ...DEFAULT_CONFIG.sensitiveHeaders,
      ...(customConfig.sensitiveHeaders ?? []),
    ]),
  }

  return (event: AnyEvent): AnyEvent => sanitizeGroundcoverEvent(event, mergedConfig)
}
